/*
 * FleetControl Connect client — the admin app's transport to vitesse-fleet-control
 * (reverse-proxied by the cloud api-gateway).
 *
 * LIBRARY-GRADE BOUNDARY (mirrors app-ui/src/core/bridge/connect-client.ts):
 * depends ONLY on @connectrpc/* + @vitesse/proto + env. No Lit, no DOM, no auth SDK —
 * auth is a token CALLBACK the host (main thread) wires the IdP token to. Unlike the gateway
 * app this runs on the MAIN thread: there's no high-frequency data plane here (a
 * handful of unary control RPCs), so the backend-worker indirection is unjustified.
 */
import { Code, ConnectError, createClient, type Interceptor, type Transport } from '@connectrpc/connect';
import { createConnectTransport } from '@connectrpc/connect-web';
import {
  type CloudServer as PbCloudServer,
  FleetControl,
  GatewayStatus,
  type GatewayRecord,
} from '@vitesse/proto/vitesse/v1/fleet_control_pb.js';
import { IdsMode, IdsStatus } from '@vitesse/proto/vitesse/v1/ids_pb.js';
import { CLOUD_API_GATEWAY_URL, lanCandidates, type LanRole } from './env';

/** Mutable token holder — the host updates `.token`; the interceptor reads it.
 *  `refresh` fetches a FRESH token on demand (the recovery path when the pushed
 *  token went stale and a request comes back `unauthenticated`). */
export interface TokenHolder {
  token: string | null;
  refresh?: () => Promise<string | null>;
}

/** Sets `Authorization: Bearer <jwt>` on every request. Org scope is resolved
 *  server-side from the verified JWT; the client never sends an org or gateway id. */
function headerInterceptor(holder: TokenHolder): Interceptor {
  return (next) => async (req) => {
    if (holder.token) req.header.set('Authorization', `Bearer ${holder.token}`);
    return next(req);
  };
}

/** On `unauthenticated`, pull a FRESH token via holder.refresh and retry ONCE — so a
 *  stale JWT (throttled refresh while the tab was hidden) self-heals instead of
 *  surfacing as a failed click. Unary only (every FleetControl RPC is unary). */
function authRetryInterceptor(holder: TokenHolder): Interceptor {
  return (next) => async (req) => {
    try {
      return await next(req);
    } catch (err) {
      const unauth = err instanceof ConnectError && err.code === Code.Unauthenticated;
      if (!unauth || req.stream || !holder.refresh) throw err;
      const fresh = await holder.refresh();
      if (!fresh) throw err;
      holder.token = fresh;
      req.header.set('Authorization', `Bearer ${fresh}`);
      return await next(req);
    }
  };
}

/** Build the FleetControl transport (cloud api-gateway; dev = same-origin via the
 *  Vite proxy). authRetry wraps OUTSIDE headerInterceptor so its retry re-runs with
 *  the refreshed Authorization header. */
export function makeFleetTransport(holder: TokenHolder): Transport {
  return createConnectTransport({
    baseUrl: CLOUD_API_GATEWAY_URL,
    interceptors: [authRetryInterceptor(holder), headerInterceptor(holder)],
  });
}

// ── Per-gateway transport: race LAN ↔ cloud, IDENTICAL contract on every leg ──
//
// A gateway's edge api-gateway (reached directly on the customer LAN) serves the
// EXACT SAME Connect services as the cloud api-gateway (the same binary; cloud just
// proxies to the box). So "LAN vs cloud" is ONLY a choice of base URL — zero protocol
// branching above the transport. We race the per-NIC LAN legs ahead of cloud and hand
// back a standard Transport to the winner; every consumer uses the same generated
// clients against it, oblivious to the leg.
//
// REUSES app-ui's mechanism (env.lanCandidates): the LAN legs are the FIXED
// per-NIC Plex names `<role>.<fp>.gateway.vitesseautomation.ai` (Cloudflare A-records
// → the box's real LAN IP, covered by its `*.<fp>...` wildcard cert). NO dashed-IP, NO
// custom DNS server, NO legacy domain. Cloud is the default + guaranteed path; a LAN leg is a
// pure latency / egress-saving upgrade.

/** A connection leg: a host-NIC LAN role (it/ot/wifi) or the cloud front door. */
export type GatewayLeg = LanRole | 'cloud';

export interface GatewayCandidate {
  leg: GatewayLeg;
  baseUrl: string;
}

/** Minimal gateway shape the race needs (a subset of FleetGateway): the fingerprint +
 *  the box's per-NIC LAN IPs (`localIp` is the IT NIC). */
type GatewayAddr = { deviceFingerprint: string; localIp: string; otIp: string; wifiIp: string };

/** Reachable base URLs for a gateway's api-gateway, LAN-first then cloud. The LAN legs
 *  come from the shared lanCandidates() (per-NIC Plex names → the box's real LAN IPs);
 *  cloud is the shared front door proxying to the same box. */
export function gatewayCandidates(gw: GatewayAddr): GatewayCandidate[] {
  const out: GatewayCandidate[] = lanCandidates(gw.deviceFingerprint, {
    itIp: gw.localIp,
    otIp: gw.otIp,
    wifiIp: gw.wifiIp,
  }).map((l) => ({ leg: l.role, baseUrl: l.url }));
  out.push({ leg: 'cloud', baseUrl: CLOUD_API_GATEWAY_URL });
  return out;
}

/** Probe a base URL for reachability; reject if unreachable. Caller-supplied so the
 *  race can probe with a REAL cheap RPC the api-gateway serves on every leg, rather
 *  than a guessed health endpoint. */
export type LegProbe = (baseUrl: string, signal: AbortSignal) => Promise<void>;

/** Race the candidate legs; resolve with the FIRST the probe confirms reachable,
 *  preferring earlier legs via a small staggered head start (a live LAN beats cloud,
 *  but a dead LAN doesn't delay the cloud fallback). Rejects only if EVERY leg fails;
 *  since cloud is normally reachable this resolves to 'cloud' when LAN/public aren't —
 *  graceful by construction, the LAN leg only ever fails CLOSED (loses the race). */
export async function raceGatewayLeg(
  gw: GatewayAddr,
  probe: LegProbe,
  opts: { headStartMs?: number; timeoutMs?: number } = {},
): Promise<GatewayCandidate> {
  const cands = gatewayCandidates(gw);
  const headStartMs = opts.headStartMs ?? 200;
  const timeoutMs = opts.timeoutMs ?? 4000;
  const attempts = cands.map((c, i) => (async () => {
    if (i > 0) await new Promise((r) => setTimeout(r, i * headStartMs)); // earlier legs get a head start
    const ac = new AbortController();
    const t = setTimeout(() => ac.abort(), timeoutMs);
    try {
      await probe(c.baseUrl, ac.signal);
      return c;
    } finally {
      clearTimeout(t);
    }
  })());
  return Promise.any(attempts);
}

// ── decoded domain type — no protobuf/bigint/byte fields leak past this boundary ──

/** A decoded fleet gateway record — the shape the fleet UI consumes. */
export interface FleetGateway {
  /** The permanent device-key fingerprint — the box identity (claim-stable). */
  deviceFingerprint: string;
  /** Owning org (IdP org id); '' = enrolled-but-unclaimed. */
  orgId: string;
  /** User-set label ('' if none). */
  displayName: string;
  /** Live presence: the backend's leaf connect/disconnect when it's definitive,
   *  else derived from the heartbeat age (see deriveStatus). */
  status: 'online' | 'offline' | 'unknown';
  /** Self-reported advisory metadata (never trusted for auth). */
  softwareVersion: string;
  hostname: string;
  /** Coarse geo (city, e.g. "Paris, FR"), resolved cloud-side from the public IP. */
  geo: string;
  /** Lat/lng (geo-resolved from the public IP) for real map placement. 0 = unresolved. */
  lat: number;
  lng: number;
  /** 'edge' | 'cloud' | ''. */
  nodeKind: string;
  /** Kill-switched (revoked) — credentials void, box can't reconnect. */
  revoked: boolean;
  /** First/last seen, ms since epoch (truncated from the registry's ns). */
  firstSeenMs: number;
  lastSeenMs: number;
  // ── network legs (NetworkInfo, cloud-observed) — '' when unknown ──
  /** IT/LAN address (NetworkInfo.itIp, falls back to NodeMetadata.localIp). */
  localIp: string;
  /** WAN address the box was last seen from (NetworkInfo.publicIp). */
  publicIp: string;
  /** OT-network address (NetworkInfo.otIp). */
  otIp: string;
  /** Wi-Fi address (NetworkInfo.wifiIp). */
  wifiIp: string;
  /** Per-role NIC MAC addresses (NetworkInfo.{it,ot,wifi}Mac) — '' when unknown. */
  itMac: string;
  otMac: string;
  wifiMac: string;
  /** Entitlement tier (Entitlement.tier) — '' when none. */
  tier: string;
  // ── host health (HostHealth, merged cloud-side) — 0 = unavailable / no data ──
  /** When the health snapshot below was SAMPLED (ms since epoch; 0 = never received).
   *  Every reading here is last-known, not current: health reaches the cloud over the
   *  box's data-plane leaf, which can be down while the box still enrolls over HTTPS —
   *  so `lastSeen` says seconds and these can be hours old. Without the sample time
   *  there is no way to tell, which is how a two-hour-old CPU reading ended up drawn
   *  as a live dial. */
  healthTsMs: number;
  /** Host CPU utilization %, 0-100. */
  cpuPct: number;
  /** Host RAM used %, 0-100. */
  memPct: number;
  /** Host RAM used / total, GiB (absolute) — the Hardware "Memory" field. 0 = unavailable. */
  memUsedGb: number;
  memTotalGb: number;
  /** Hottest sensor °C, absolute. */
  tempC: number;
  /** Host uptime, seconds. */
  uptimeS: number;
  // ── richer host telemetry (procfs/sysfs/RAPL; 0/'' = unavailable) ──
  /** Root/data filesystem used / total, GiB. */
  diskUsedGb: number;
  diskTotalGb: number;
  /** Running process count. */
  processCount: number;
  /** 1-min load average (absolute); divide by cpuCores for %. */
  load1: number;
  /** Logical CPU cores. */
  cpuCores: number;
  /** Package power draw, watts (Intel RAPL; 0 = no sensor). */
  powerW: number;
  /** rx+tx packet-drop %, 0-100 (0 = none). */
  packetDropPct: number;
  /** Host OS pretty-name. */
  os: string;
  /** Co-located NATS broker throughput, messages/sec (in+out) — the edge "msg/s"
   *  dial + the basis for broker-load%. 0 = broker idle / monitor unreachable. */
  brokerMsgsPerSec: number;
  /** Host CPU model (e.g. "Intel N100"), from /proc/cpuinfo. '' = unknown. */
  cpuModel: string;
  /** Edge→cloud leaf-link RTT, ms (broker-measured). 0 = unknown. */
  cloudLatencyMs: number;
  /** Edge IDS (gateway-ids) — undefined when the box doesn't run the IDS. */
  ids?: GatewayIds;
}

/** A decoded IDS signature hit (top-N by count). */
export interface IdsSignature {
  sid: number;
  name: string;
  count: number;
  severity: number; // 1=info … 5=critical (AlertSeverity)
}
/** A decoded active firewall block. */
export interface IdsBlock {
  srcIp: string;
  iface: number; // 1=IT, 2=OT
  reason: number; // 1=auto, 2=manual
  signatureId: number;
}

/** The decoded edge-IDS surface (descriptor + rich stats) for the UI's security
 *  analytics. All counts are cumulative since the engine started unless noted. */
export interface GatewayIds {
  mode: string; // 'off' | 'monitor' | 'protect' | ''
  status: string; // 'running' | 'degraded' | 'starting' | 'error' | ''
  suricataAlive: boolean;
  suricataVersion: string;
  engineUptimeS: number;
  // capture / decoder
  packetsTotal: number;
  bytesTotal: number;
  kernelDrops: number;
  /** Suricata process CPU % + RSS in MB — the security software's resource use (dials). */
  idsCpuPct: number;
  idsMemMb: number;
  /** Packets inspected per second (IT+OT, /5s interval) — live data-plane throughput (dial). */
  inspectedPerSec: number;
  invalidPackets: number;
  // flows / sessions
  flowsActive: number;
  flowsTotal: number;
  flowMemuseBytes: number;
  tcpSessions: number;
  // alerts
  alertsTotal: number;
  alertsInInterval: number;
  alertsCritical: number;
  alertsHigh: number;
  alertsMedium: number;
  topSignatures: IdsSignature[];
  topCategories: { category: string; count: number }[];
  topSources: { ip: string; count: number }[];
  appLayerFlows: { proto: string; count: number }[]; // incl. OT: modbus/dnp3/enip/s7comm
  // active response
  blocksTotal: number;
  activeBlocks: IdsBlock[];
}

const FLEET_STATUS: Record<GatewayStatus, FleetGateway['status']> = {
  [GatewayStatus.ONLINE]: 'online',
  [GatewayStatus.OFFLINE]: 'offline',
  [GatewayStatus.UNSPECIFIED]: 'unknown',
};

/**
 * A gateway last seen within this window counts as online. The edge heartbeat cadence
 * is ~45s (vitesse-fleet-node), so this is ~3 missed beats. Used ONLY when status is
 * UNSPECIFIED:
 * the cloud control-plane doesn't yet translate leaf connect/disconnect into
 * ONLINE/OFFLINE (the unbuilt claim-service), so the heartbeat is today's liveness
 * signal. A definitive ONLINE/OFFLINE from the backend always wins over this.
 */
const ONLINE_WINDOW_MS = 150_000;

/** Backend presence if it's definitive; otherwise derive it from the heartbeat age. */
function deriveStatus(backend: GatewayStatus, lastSeenMs: number): FleetGateway['status'] {
  const mapped = FLEET_STATUS[backend] ?? 'unknown';
  if (mapped !== 'unknown') return mapped; // backend knows → trust it
  if (!lastSeenMs) return 'unknown'; // never seen
  return Date.now() - lastSeenMs <= ONLINE_WINDOW_MS ? 'online' : 'offline';
}

export function toFleetGateway(r: GatewayRecord): FleetGateway {
  const lastSeenMs = Number(r.lastSeenNs / 1_000_000n);
  return {
    deviceFingerprint: r.deviceFingerprint,
    orgId: r.orgId,
    displayName: r.displayName,
    status: deriveStatus(r.status, lastSeenMs),
    softwareVersion: r.metadata?.softwareVersion ?? '',
    hostname: r.metadata?.hostname ?? '',
    geo: r.network?.geoCity || r.metadata?.geo || '',
    lat: r.network?.latitude ?? 0,
    lng: r.network?.longitude ?? 0,
    nodeKind: r.metadata?.nodeKind ?? '',
    revoked: r.revoked,
    firstSeenMs: Number(r.firstSeenNs / 1_000_000n),
    lastSeenMs,
    localIp: r.network?.itIp || r.metadata?.localIp || '',
    publicIp: r.network?.publicIp ?? '',
    otIp: r.network?.otIp ?? '',
    wifiIp: r.network?.wifiIp ?? '',
    itMac: r.network?.itMac ?? '',
    otMac: r.network?.otMac ?? '',
    wifiMac: r.network?.wifiMac ?? '',
    tier: r.entitlement?.tier ?? '',
    healthTsMs: Number(r.hostHealth?.timestampNs ?? 0n) / 1e6,
    cpuPct: r.hostHealth?.cpuPct ?? 0,
    memPct: r.hostHealth?.memPct ?? 0,
    memUsedGb: r.hostHealth?.memUsedGb ?? 0,
    memTotalGb: r.hostHealth?.memTotalGb ?? 0,
    tempC: r.hostHealth?.tempC ?? 0,
    uptimeS: Number(r.hostHealth?.uptimeS ?? 0n),
    diskUsedGb: r.hostHealth?.diskUsedGb ?? 0,
    diskTotalGb: r.hostHealth?.diskTotalGb ?? 0,
    processCount: r.hostHealth?.processCount ?? 0,
    load1: r.hostHealth?.load1 ?? 0,
    cpuCores: r.hostHealth?.cpuCores ?? 0,
    powerW: r.hostHealth?.powerW ?? 0,
    packetDropPct: r.hostHealth?.packetDropPct ?? 0,
    os: r.hostHealth?.os ?? '',
    brokerMsgsPerSec: r.hostHealth?.brokerMsgsPerSec ?? 0,
    cpuModel: r.hostHealth?.cpuModel ?? '',
    cloudLatencyMs: r.hostHealth?.cloudLatencyMs ?? 0,
    ids: decodeIds(r),
  };
}

const IDS_MODE: Record<number, string> = {
  [IdsMode.OFF]: 'off',
  [IdsMode.MONITOR]: 'monitor',
  [IdsMode.PROTECT]: 'protect',
};
const IDS_STATUS: Record<number, string> = {
  [IdsStatus.STARTING]: 'starting',
  [IdsStatus.RUNNING]: 'running',
  [IdsStatus.DEGRADED]: 'degraded',
  [IdsStatus.ERROR]: 'error',
};

/** Decode the IDS descriptor + stats off a GatewayRecord into the UI shape.
 *  undefined when the box runs no IDS (neither key posted). */
function decodeIds(r: GatewayRecord): GatewayIds | undefined {
  const d = r.ids;
  const s = r.idsStats;
  if (!d && !s) return undefined;
  const appLayer = Object.entries(s?.appLayerFlows ?? {})
    .map(([proto, count]) => ({ proto, count: Number(count) }))
    .filter((x) => x.count > 0)
    .sort((a, b) => b.count - a.count);
  return {
    mode: d ? (IDS_MODE[d.mode] ?? '') : '',
    status: d ? (IDS_STATUS[d.status] ?? '') : '',
    suricataAlive: d?.suricataAlive ?? false,
    suricataVersion: d?.suricataVersion ?? '',
    engineUptimeS: Number(s?.engineUptimeS ?? 0n),
    packetsTotal: Number(s?.packetsTotal ?? 0n),
    bytesTotal: Number(s?.bytesTotal ?? 0n),
    kernelDrops: Number(s?.kernelDropsTotal ?? 0n),
    // the security software's own resource use + data-plane throughput — dial-worthy
    // (real, varying, non-zero) where alerts are 0-ish (→ text fields).
    idsCpuPct: s?.cpuPercent ?? 0,
    idsMemMb: Number(s?.memoryRssBytes ?? 0n) / 1e6,
    inspectedPerSec: (Number(s?.packetsInspectedItInInterval ?? 0n) + Number(s?.packetsInspectedOtInInterval ?? 0n)) / 5,
    invalidPackets: Number(s?.invalidPackets ?? 0n),
    flowsActive: Number(s?.flowsActive ?? 0n),
    flowsTotal: Number(s?.flowsTotal ?? 0n),
    flowMemuseBytes: Number(s?.flowMemuseBytes ?? 0n),
    tcpSessions: Number(s?.tcpSessions ?? 0n),
    alertsTotal: Number(s?.alertsTotalSinceStart ?? 0n),
    alertsInInterval: Number(s?.alertsInInterval ?? 0n),
    alertsCritical: Number(s?.alertsCritical ?? 0n),
    alertsHigh: Number(s?.alertsHigh ?? 0n),
    alertsMedium: Number(s?.alertsMedium ?? 0n),
    topSignatures: (s?.topSignatures ?? []).map((x) => ({
      sid: Number(x.signatureId),
      name: x.name,
      count: Number(x.count),
      severity: x.severity,
    })),
    topCategories: (s?.topCategories ?? []).map((x) => ({ category: x.category, count: Number(x.count) })),
    topSources: (s?.topSources ?? []).map((x) => ({ ip: x.srcIp, count: Number(x.count) })),
    appLayerFlows: appLayer,
    blocksTotal: Number(s?.blocksAppliedSinceStart ?? 0n),
    activeBlocks: (s?.activeBlocks ?? []).map((b) => ({
      srcIp: b.srcIp,
      iface: b.interface,
      reason: b.reason,
      signatureId: Number(b.signatureId),
    })),
  };
}

/**
 * A private cloud server — one of OUR Hetzner boxes running the stack. A
 * self-locating, self-reporting NODE (same shape idea as an edge gateway), distinct
 * on the map. Edges leaf-connect to one; the arcs run edge → its server.
 *
 * ACCESS (enforced server-side): a Vitesse super-admin sees every server WITH stats;
 * an org-admin sees only the server(s) their gateways bind to, WITHOUT stats —
 * `hasStats=false` and the metric fields are 0. The monthly Hetzner network figure is
 * deferred (needs an HCLOUD token); cpu/mem/disk/geo are host-read, no token.
 */
export interface CloudServer {
  /** Stable server id (device-fingerprint-style on the real box). */
  id: string;
  /** Human label — the region/city ("Nuremberg"). */
  name: string;
  /** Coarse geo (city), self-resolved from the box's own public IP. */
  geo: string;
  lat: number;
  lng: number;
  /** Did THIS viewer receive stats? (super-admin) — drives whether the panel shows them. */
  hasStats: boolean;
  /** Host CPU %, 0-100 (0 when !hasStats). */
  cpuPct: number;
  /** RAM used / total, GiB (0 when !hasStats). */
  memUsedGb: number;
  memTotalGb: number;
  /** Disk used / total, GiB (0 when !hasStats). */
  diskUsedGb: number;
  diskTotalGb: number;
  // ── richer host telemetry (all 0/'' when !hasStats) ──
  /** Host uptime, seconds. */
  uptimeS: number;
  /** Load average (1 / 5 / 15 min). */
  load1: number;
  load5: number;
  load15: number;
  /** CPU model string, e.g. "AMD EPYC 9454P". */
  cpuModel: string;
  /** Logical core count. */
  cpuCores: number;
  /** OS pretty-name, e.g. "Ubuntu 24.04 LTS". */
  os: string;
  /** Kernel version, e.g. "6.8.0-31-generic". */
  kernel: string;
  /** Architecture, e.g. "x86_64". */
  arch: string;
  /** Running process count. */
  processCount: number;
  /** Gateways currently online/bound to the fleet (cloud-derived). */
  activeConnections: number;
  /** Registry rows (gateway→org records). */
  dbEntries: number;
  /** Package power draw, watts (0 on VMs — no sensor). */
  powerW: number;
  /** Monthly outbound traffic as % of the included cap (Hetzner API). 0 = unavailable. */
  egressPct: number;
  /** FleetControl RPC success / error rates, 0-100. 0 = no data yet. */
  apiSuccessPct: number;
  errorRatePct: number;
  /** Mean FleetControl RPC handler latency, ms. 0 = no data yet. (vestigial — UI dropped it) */
  avgLatencyMs: number;
  /** Monthly inbound traffic as % of the included cap (Hetzner API). 0 = unavailable. */
  ingressPct: number;
  /** Hetzner server type, e.g. "ccx33". '' = unknown. (vestigial — UI dropped the field) */
  serverType: string;
  /** Co-located cloud broker throughput (in+out msgs/sec) from /varz. 0 = idle/unreachable. */
  brokerMsgsPerSec: number;
}

/** Decode a protobuf CloudServer → the UI shape (the server already applied access:
 *  a non-super-admin viewer receives has_stats=false + zeroed metrics). */
export function toCloudServer(c: PbCloudServer): CloudServer {
  return {
    id: c.id,
    name: c.name,
    geo: c.geo,
    lat: c.lat,
    lng: c.lng,
    hasStats: c.hasStats,
    cpuPct: c.cpuPct,
    memUsedGb: c.memUsedGb,
    memTotalGb: c.memTotalGb,
    diskUsedGb: c.diskUsedGb,
    diskTotalGb: c.diskTotalGb,
    uptimeS: Number(c.uptimeS),
    load1: c.load1,
    load5: c.load5,
    load15: c.load15,
    cpuModel: c.cpuModel,
    cpuCores: c.cpuCores,
    os: c.os,
    kernel: c.kernel,
    arch: c.arch,
    processCount: c.processCount,
    activeConnections: c.activeConnections,
    dbEntries: Number(c.dbEntries),
    powerW: c.powerW,
    egressPct: c.egressPct,
    apiSuccessPct: c.apiSuccessPct,
    errorRatePct: c.errorRatePct,
    avgLatencyMs: c.avgLatencyMs,
    ingressPct: c.ingressPct,
    serverType: c.serverType,
    brokerMsgsPerSec: c.brokerMsgsPerSec,
  };
}

// ── RPCs ─────────────────────────────────────────────────────────────────────
// All cloud-scoped; org scope comes from the verified JWT. ListFleet returns ALL
// orgs for a super-admin (admin role in the Vitesse org), else just the caller's
// org — the server decides; the body carries no org.

// (listFleet removed — the fleet roster is the LIVE SubscribeFleet stream, owned by
// FleetManager. One-shot listing is dead; nothing calls it.)

/** Enrolled-but-unclaimed gateways on the caller's own network ("claim a box near
 *  me"). The network is the cloud-observed source IP, so the request is empty. */
export async function discoverNearby(transport: Transport): Promise<FleetGateway[]> {
  const client = createClient(FleetControl, transport);
  const reply = await client.discoverNearby({});
  return reply.gateways.map(toFleetGateway);
}

/** Claim an enrolled gateway BY FINGERPRINT + CODE — the remote/off-LAN path.
 *  `discoverNearby`'s one-click quick-claim only works when the caller's browser
 *  shares a network with the box; anything else (a cloud-hosted or otherwise
 *  off-site gateway) has no net-hash match and MUST prove itself with the code
 *  the box displays. Same server-side RPC as quick-claim — the code is simply
 *  the second of the two proofs `Claim` accepts (see fleet-control's server.go). */
export async function claimGateway(
  transport: Transport,
  deviceFingerprint: string,
  claimCode: string,
  displayName?: string,
): Promise<FleetGateway> {
  const client = createClient(FleetControl, transport);
  const reply = await client.claim({
    deviceFingerprint,
    claimCode,
    displayName: displayName ?? '',
  });
  if (!reply.record) throw new Error('claim returned no record');
  return toFleetGateway(reply.record);
}

/** Release a gateway from its org (owner-or-super-admin). Data is untouched — this
 *  is a metadata flip; the box becomes unowned until re-claimed. */
export async function unclaimGateway(transport: Transport, deviceFingerprint: string): Promise<FleetGateway> {
  const client = createClient(FleetControl, transport);
  const reply = await client.unclaim({ deviceFingerprint });
  if (!reply.record) throw new Error('unclaim returned no record');
  return toFleetGateway(reply.record);
}

/** Kill-switch a gateway (SUPER-ADMIN only) — voids its credentials so it can't
 *  reconnect. `reason` is for the audit trail. */
export async function revokeGateway(
  transport: Transport,
  deviceFingerprint: string,
  reason: string,
): Promise<FleetGateway> {
  const client = createClient(FleetControl, transport);
  const reply = await client.revoke({ deviceFingerprint, reason });
  if (!reply.record) throw new Error('revoke returned no record');
  return toFleetGateway(reply.record);
}

/** PERMANENTLY remove a gateway from the registry (erases the record + drops its
 *  gw→org binding). Super-admin removes any; an org-admin only their own org's —
 *  enforced server-side. Idempotent. */
export async function removeGateway(transport: Transport, deviceFingerprint: string): Promise<void> {
  const client = createClient(FleetControl, transport);
  await client.removeGateway({ deviceFingerprint });
}

/** Carry a person's "request access" ask to the org's admins (the gated-UI
 *  overlay's Request-access button). `permissionKey` is the org-scoped permission
 *  the wall named; `context` is where they hit it (a view name or entity path),
 *  shown to the admin so the ask is legible. Server notifies the org's admins;
 *  the overlay already flipped to "sent" optimistically, so only a real failure
 *  needs surfacing. Mirrors app-ui's connect-client.requestAccess. */
export async function requestAccess(transport: Transport, permissionKey: string, context: string): Promise<void> {
  const client = createClient(FleetControl, transport);
  await client.requestAccess({ permissionKey, context });
}

/** Rename a gateway to a human-readable name — the label shown here AND in the
 *  gateway app (both read the same registry record). Super-admin: any; org-admin:
 *  own org only (server-enforced). Empty clears it. Returns the updated gateway. */
export async function setDisplayName(
  transport: Transport,
  deviceFingerprint: string,
  displayName: string,
): Promise<FleetGateway> {
  const client = createClient(FleetControl, transport);
  const reply = await client.setDisplayName({ deviceFingerprint, displayName });
  if (!reply.record) throw new Error('setDisplayName returned no record');
  return toFleetGateway(reply.record);
}

